import { ElementStatus, hasImageExtension, isAiSupportedImageFormat, originalImageUrl } from '@wix/rules-panel-ui';
import { useCallback, useMemo, useRef, useState } from 'react';
const transformToImageData = img => {
  const displayUrl = originalImageUrl(img.url);
  return {
    altText: img.alt ?? '',
    breakpointId: img.breakpointId,
    builderLabel: img.builderLabel,
    componentId: img.componentId,
    entityId: img.entityId,
    id: img.id,
    isAiSupported: isAiSupportedImageFormat(displayUrl),
    isDecorative: img.isDecorative ?? false,
    isLinked: img.isLinked,
    status: img.status,
    url: displayUrl
  };
};
export const useImageState = _ref => {
  let {
    altRulesPropsWithState,
    setShowSummary,
    setSelectedImageId
  } = _ref;
  // Keep original props map for tpaData reference when saving
  const originalPropsMap = useMemo(() => new Map(altRulesPropsWithState.map(img => [img.id, img])), [altRulesPropsWithState]);

  // Local state
  const [images, setImages] = useState(() => altRulesPropsWithState.map(transformToImageData));
  const [modifiedImageIds, setModifiedImageIds] = useState(new Set());
  const [hasChanges, setHasChanges] = useState(false);
  const [hasAiGeneratedContent, setHasAiGeneratedContent] = useState(false);

  // Store original images for reset/discard functionality
  const [originalImages, setOriginalImages] = useState(() => altRulesPropsWithState.map(transformToImageData));

  // Calculate counts — exclude images whose alt text contains a file extension.
  const filledCount = images.filter(img => img.altText && !hasImageExtension(img.altText) || img.isDecorative && !img.isLinked).length;
  const missingCount = images.length - filledCount;
  const hasModifiedImages = modifiedImageIds.size > 0;

  // Keep a ref to current images to avoid stale closure in commitCurrentAsOriginal
  const imagesRef = useRef(images);
  imagesRef.current = images;

  // Reset to original images (discard changes)
  // Matches original production behavior by also resetting UI state
  const resetToOriginal = useCallback(() => {
    setImages(originalImages);
    setModifiedImageIds(new Set());
    setHasChanges(false);
    setHasAiGeneratedContent(false);
    setShowSummary == null || setShowSummary(false);
    setSelectedImageId == null || setSelectedImageId(null);
  }, [originalImages, setShowSummary, setSelectedImageId]);

  // Commit current images as the new original baseline
  // Called after successful save so that discard reverts to post-save state
  // Uses ref to ensure we always have the latest images state
  const commitCurrentAsOriginal = useCallback(() => {
    setOriginalImages(imagesRef.current);
  }, []);

  // Update alt text handler
  const handleUpdateAltText = useCallback((id, value) => {
    setImages(prev => prev.map(img => img.id === id ? {
      ...img,
      altText: value,
      // For linked images `isDecorative` doesn't grant a passing
      // status — only valid alt text does.
      status: value && !hasImageExtension(value) || img.isDecorative && !img.isLinked ? ElementStatus.Passed : ElementStatus.Failed
    } : img));
    setHasChanges(true);
    setModifiedImageIds(prev => new Set(prev).add(id));
  }, []);

  // Update decorative handler
  const handleUpdateDecorative = useCallback((id, isDecorative) => {
    setImages(prev => {
      // A linked image must not become decorative — the link needs an
      // accessible name. The UI checkbox is already disabled; this guards
      // any non-UI caller.
      const target = prev.find(img => img.id === id);
      if (target != null && target.isLinked && isDecorative) {
        return prev;
      }
      return prev.map(img => img.id === id ? {
        ...img,
        altText: isDecorative ? '' : img.altText,
        isDecorative,
        // When decorative, always passed. When not decorative, check if alt text exists
        status: isDecorative || img.altText ? ElementStatus.Passed : ElementStatus.Failed
      } : img);
    });
    setHasChanges(true);
    setModifiedImageIds(prev => new Set(prev).add(id));
  }, []);
  return {
    commitCurrentAsOriginal,
    filledCount,
    handleUpdateAltText,
    handleUpdateDecorative,
    hasAiGeneratedContent,
    hasChanges,
    hasModifiedImages,
    images,
    missingCount,
    modifiedImageIds,
    originalImages,
    originalPropsMap,
    resetToOriginal,
    setHasAiGeneratedContent,
    setHasChanges,
    setImages,
    setModifiedImageIds
  };
};
//# sourceMappingURL=use-image-state.js.map